import { IpaType, getAllIpa } from "./ipa";

/**
 * attempt to take an almost-IPA string & figure out what actual IPA was intended,
 * NOTE: only does simple, single character replacements
 */
export function cleanupIpa(raw: string): { ipa: IpaType, unknown: string[] } {
  let ipa: IpaType = '';
  const unknown: string[] = [];
  const allowed = getRecognizedIpa();
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    const attempt = asAllowed(c, allowed);
    if (attempt === undefined) {
      unknown.push(c);
    } else {
      ipa = ipa.concat(attempt);
    }
  }
  return { ipa, unknown };
}

/** get all IPA that we recognize */
export function getRecognizedIpa(): Set<IpaType> {
  const set: Set<IpaType> = new Set();
  const { ipa, diacritics } = getAllIpa();
  ipa.forEach(c => set.add(c));
  diacritics.forEach(c => set.add(c));
  return set;
}

/** convert to IPA if we can */
function asAllowed(c: string, allowed: Set<IpaType>): string | undefined {
  if (allowed.has(c)) {
    return c;
  }

  const lower = c.toLowerCase();
  if (allowed.has(lower)) {
    return lower;
  }

  const other = lookup[lower];
  if (other !== undefined) {
    return other;
  }

  return undefined;
}

export function getCleanupSymbols(): { input: string[], output: IpaType[] } {
  return { input: Object.keys(lookup), output: Object.values(lookup) };
}

/** simple map of one non-IPA character to valid IPA */
export function getCleanupLookup(): Record<string, IpaType> {
  return lookup;
}

/** map of multiple characters (may include valid IPA, but isn't valid as a whole) to IPA */
export function getMultiCleanupLookup(): Record<string, IpaType> {
  return multiLookup;
}

/** unambiguous IPA replacements (a subset of 'lookup' below) */
export const almostIpa: Record<string, IpaType> = {
  "'": "ˈ", // primary stress
  ':': 'ː', // lengthen
  '’': 'ʼ', // ejective

  // lookalikes
  ε: 'ɛ', φ: 'ɸ', ǝ: 'ə', ә: 'ə',
  ä: 'ä', ë: 'ë', ï: 'ï', ö: 'ö', ü: 'ü',
  // nasals
  ã: 'ã', ẽ: 'ẽ', ĩ: 'ĩ', õ: 'õ', ũ: 'ũ', ỹ: 'ỹ',
  // obvious ligatures
  ʦ: 't͡s', ʣ: 'd͡z', ʧ: 't͡ʃ', ʤ: 'd͡ʒ', ʨ: 't͡ɕ', ʥ: 'd͡ʑ',
}

/** single non-IPA character to valid IPA */
const lookup: Record<string, IpaType> = {
  ' ': ' ', '\t': ' ',

  "'": "ˈ", ',': 'ˌ', '″': "ˈ",
  ':': 'ː',

  // https://en.wikipedia.org/wiki/Macron_below
  ḇ: 'v', // or β
  ḏ: 'ð', // or ɖ
  ẖ: 'x', // or χ
  ḵ: 'χ', // or q
  ḻ: 'l', // or ɬ
  ṉ: 'ɳ', // or ɴ
  ṟ: 'ɻ', // or ɽ
  ṯ: 'θ', // or ʈ or t̪
  ẕ: 't͡s',

  // https://en.wikipedia.org/wiki/Obsolete_and_nonstandard_symbols_in_the_International_Phonetic_Alphabet
  '?': 'ʔ', 7: 'ʔ',
  ȿ: 'sʷ', σ: 'sʷ', ɀ: 'zʷ', ƍ: 'zʷ',  // or [s͎] [z͎], for Shona
  ƺ: 'ʒᶣ', // or [ʑʷ] or [ʒʷ]
  ƪ: 'ʃᶣ', // or [ɕʷ] or [ʃʷ]
  ƻ: 'd͡z',
  ƾ: 't͡s',
  // palatalized
  ᶀ: 'bʲ', 'ꞔ': 'cʲ', ᶁ: 'dʲ', ᶂ: 'fʲ', ᶃ: 'gʲ', 'ꞕ': 'hʲ',
  ᶄ: 'kʲ', ᶅ: 'lʲ', ᶆ: 'mʲ', ᶇ: 'nʲ', ᶈ: 'pʲ', ᶉ: 'rʲ',
  ᶊ: 'sʲ', ᶋ: 'ʃʲ', ƫ: 'tʲ', ᶌ: 'vʲ', ᶍ: 'xʲ', ᶎ: 'zʲ',
  '𝼓': 'ɬʲ', '𝼔': 'ŋʲ', '𝼖': 'ɾʲ', '𝼕': 'ɹʲ',  // from https://en.wikipedia.org/wiki/Palatalization_(phonetics)
  // r-colored vowels
  ᶏ: 'a˞', ᶐ: 'ɑ˞', ᶒ: 'e˞', ᶓ: 'ɛ˞', ᶔ: 'ɜ˞',
  ᶕ: 'ə˞', ᶖ: 'i˞', ᶗ: 'ɔ˞', ᶙ: 'u˞',
  ɼ: 'r̝',
  λ: 'd͡ɮ', ƛ: 't͡ɬ', ł: 'ɬ',
  č: 't͡ʃ', // or t͡ʂ
  š: 'ʃ', // or ʂ
  ž: 'ʒ', // or ʐ
  ǰ: 'd͡ʒ', ǧ: 'd͡ʒ', ǯ: 'd͡ʒ',
  ẋ: 'χ',
  ρ: 'ʙ',
  ɩ: 'ɪ', ı: 'ɪ',
  ᵼ: 'ɪ̈',
  ᵻ: 'ɨ̞', // or ɪ̈
  ᵿ: 'ʉ̞', // or ʊ̈
  ʚ: 'ɞ', // or œ
  ɷ: 'ʊ',
  ω: 'ʊ̜', // or ɯ̽, o̞, ɔ̝
  ȹ: 'p̪', ȸ: 'b̪',
  ʇ: 'ǀ', ʗ: 'ǃ', ʖ: 'ǁ', '𝼊': '‼', ψ: '‼', '⨎': 'ǂ',
  ȣ: 'ɤ', // or ɣ
  я: 'ʢ', // or ʀ̠ 
  ɿ: 'ɹ̩', ʅ: 'ɻ̩', ʮ: 'ɹ̩ʷ', ʯ: 'ɻ̩ʷ', // or /z̩ ʐ̩ z̩ʷ ʐ̩ʷ/ or /ɯ ɨ u ʉ/ or /. ɨ˞ . ʉ˞/
  ᴀ: 'ä', ᴇ: 'e̞', ⱻ: 'ɤ̘',
  Ω: 'ɔ̝',
  ᴜ: 'ʊ', // or ʊ̹
  Q: 'ʡ',
  ʦ: 't͡s', ʣ: 'd͡z', ʧ: 't͡ʃ', ʤ: 'd͡ʒ', ʨ: 't͡ɕ', ʥ: 'd͡ʑ',
  ȼ: 't͡s',
  // diacritics
  '\u032B': 'ʷ', // e.g. [k̫]
  '\u0316': '˧˩', '\u0317': '˩˧', // lower tone falling/rising, e.g., ɔ̖ ɔ̗
  '\u034b': '\u0303', // nasal fricative, e.g., n͋ or v͋, switch to ñ
  // https://en.wikipedia.org/wiki/Fricative
  ŝ: 'ʆ', ẑ: 'ʓ',
  ẓ: 'ʐ', // or /z̺̠/
  ṽ: 'ṽ',

  ϐ: 'β˕', δ: 'ð̞',
  ɉ: 'ɨ̯',
  'ꭥ': 'o̞',
  ʪ: 'sˡ', ʫ: 'zˡ',
  ᶘ: 'ʂ', ᶚ: 'ʐ',
  ş: 'ʃ', ţ: 't͡s',
  ⱳ: 'ⱱ̟',
  '\u0328': '\u02D5', // lowered

  α: 'ɑ', // alpha
  ą: 'ã', // varies by language
  ȷ: 'j', // dotless j, e.g., used in ȷ̃
  B: 'ʙ', G: 'ɢ', H: 'ʜ', L: 'ʟ', N: 'ɴ', R: 'ʀ', // capital look-alikes
  ε: 'ɛ', // Greek that looks just like the IPA
  φ: 'ɸ', // Greek that looks just like the IPA
  ǝ: 'ə', // African letter that looks just like the IPA
  ә: 'ə',
  ḁ: 'ḁ', // \u1e01 > /a/ + ["voiceless"]
  ẽ: 'ẽ', // \u1ebd > /e/ + ["nasalized"]
  ĩ: 'ĩ', // \u0129 > /i/ + ["nasalized"]
  ũ: 'ũ', // \u0169 > /u/ + ["nasalized"]
  ỹ: 'ỹ', // \u1ef9 > /y/ + ["nasalized"]
  ḛ: 'ḛ', ḭ: 'ḭ', ṵ: 'ṵ',
  ẅ: 'ẅ', // \u1e85 > /w/ + ["centralized"]
  ƿ: 'w', // Old English
  Φ: 'ɸ',
  ḫ: 'χ', // Hittite
  '’': 'ʼ', // ejective, \u2019 > \u02BC
  '!': 'ǃ',

  // various symbols seen in PIE, Old English, Gothic
  ǣ: 'æː', ā: 'aː', ē: 'eː', ī: 'iː',
  ō: 'oː', ū: 'uː', ȳ: 'yː',
  ě: 'e', ę: 'ẽ',
  ǵ: 'gʲ', ḱ: 'kʲ',
  ǟ: 'aː', ṓ: 'oː',
  ḿ: 'm̩', ŕ: 'r̩',
  ḍ: 'ɖ', ṛ: 'ɽ', ṣ: 'ʂ', ṭ: 'ʈ', ḥ: 'ħ',
  Þ: 'θ', þ: 'θ', // or ð
  Ƕ: 'ʍ', ƕ: 'ʍ', // or hʷ

  // upper ascii that looks like IPA with a diacritic,
  à: 'à', á: 'á', â: 'â', ã: 'ã', ä: 'ä', å: 'å',
  è: 'è', é: 'é', ê: 'ê', ë: 'ë',
  ì: 'ì', í: 'í', î: 'î', ï: 'ï',
  ò: 'ò', ó: 'ó', ô: 'ô', õ: 'õ', ö: 'ö',
  ù: 'ù', ú: 'ú', û: 'û', ü: 'ü',
  À: 'à', Á: 'á',
  È: 'è', É: 'é',
  Ì: 'ì', Í: 'í',
  Ò: 'ò', Ó: 'ó',
  Ù: 'ù', Ú: 'ú',

  ỳ: 'ỳ', ý: 'ý', ÿ: 'ÿ',
  ß: 's', // German
  Ñ: 'ɲ', ñ: 'ɲ', // Spanish
  ň: 'ɲ', // Slavic
  // misc upper ascii
  Æ: 'æ', Œ: 'ɶ', Š: 'ʃ', Ž: 'ʒ', Ÿ: 'ÿ',
  Ã: 'ã', Ä: 'ä', Ç: 'ç', Ë: 'ë', Ï: 'ï', Õ: 'õ', Ö: 'ö', Ü: 'ü',

  // Americanist, when it's a single char & doesn't interfere with IPA
  // https://en.wikipedia.org/wiki/Americanist_phonetic_notation
  ć: 't͡ɕ', ń: 'ȵ', ś: 'ɕ', ź: 'ʑ',
  ḷ: 'ɭ', ƚ: 'ɬ',
  ř: 'ɾ', // flap, r̃ is trilled
  '¢': 'c',
  ẉ: 'ɰ',
  ʸ: 'ʲ',

  // unhandled superscripts, which typically mean something more subtle than simple substitution, e.g., an affricate
  // https://en.wikipedia.org/wiki/International_Phonetic_Alphabet#Superscript_IPA
  ᵐ: 'm', ᶬ: 'ɱ', ᶯ: 'ɳ', ᶮ: 'ɲ', ᵑ: 'ŋ', ᶰ: 'ɴ',
  ᵖ: 'p', ᵇ: 'b', ᵗ: 't', ᵈ: 'd', ᶜ: 'c', ᶡ: 'ɟ', ᵏ: 'k', ᶢ: 'ɡ', ᵍ: 'g',
  ᶲ: 'ɸ', ᶠ: 'f', ᵛ: 'v', ᶿ: 'θ', ᶞ: 'ð', ˢ: 's', ᶻ: 'z', ᶴ: 'ʃ', ᶝ: 'ɕ', ᶾ: 'ʒ', ᶽ: 'ʑ', ᶳ: 'ʂ', ᶼ: 'ʐ', ᶨ: 'ʝ', ˣ: 'x', ᵡ: 'χ', ʶ: 'ʁ',
  ʵ: 'ɻ', ᶭ: 'ɰ',
  ᶵ: 'tʲ', // ƫ
  'ꭞ': 'ɫ', ᶩ: 'ɭ', ᶫ: 'ʟ',
  ꜝ: 'ǃ',
  ⁱ: 'i', ᶤ: 'ɨ', ᶶ: 'ʉ', ᵚ: 'ɯ', ᵘ: 'u',
  ᶦ: 'ɪ', ᶥ: 'ɪ', ᶧ: 'ɨ̞', /* ᵻ */  ᶷ: 'ʊ',
  ᵉ: 'e', ᶱ: 'ɵ', ᵒ: 'o',
  ᵋ: 'ɛ', ꟹ: 'œ', ᶟ: 'ɜ', ᵌ: 'ɜ', ᶺ: 'ʌ', ᵓ: 'ɔ',
  ᵄ: 'ɐ', ᵅ: 'ɑ', ᶛ: 'ɒ', ᵃ: 'a',

  // non-standard tones
  '꜌': '˩', '꜋': '˨', '꜊': '˧', '꜉': '˦', '꜈': '˥', // Chao weak tone
  '꜖': '˩', '꜕': '˨', '꜔': '˧', '꜓': '˦', '꜒': '˥', //  Chao tone sandhi
  '꜑': '˩', '꜐': '˨', '꜏': '˧', '꜎': '˦', '꜍': '˥', // Chao weak tone sandhi
  ˍ: '˩', ˉ: '˥', '\u0331': '˩',
  ˏ: '\u1dc5', // low rising
  ˊ: '\u1dc4', // high rising
  ˎ: '\u1dc6', // low falling
  ˋ: '\u1dc7', // high falling
  ˆ: '\u1dc8', // rising-falling
  ˇ: '\u1dc9', // falling-rising
  '¹': '˩',
  '²': '˨',
  '³': '˧',
  '⁴': '˦',
  '⁵': '˥',

  // upper case IPA, used by languages that have adopted some IPA
  Ɑ: 'ɑ',
  Ɒ: 'ɒ',
  Ɐ: 'ɐ',
  Ɓ: 'ɓ',
  Ƃ: 'ɓ',
  'Ꞵ': 'β', 'ꞵ': 'β', // upper & lower
  Ɔ: 'ɔ',
  Ƈ: 'ƈ',
  Ð: 'ð',
  Ɖ: 'ɖ', Ɗ: 'ɖ', // alternate upper case
  Ə: 'ə', Ǝ: 'ə', // alternate upper case
  Ɛ: 'ɛ',
  'Ɜ': 'ɜ',
  'Ɡ': 'ɡ',
  Ɠ: 'ɠ',
  Ɣ: 'ɣ',
  Ħ: 'ħ',
  Ɦ: 'ɦ',
  Ɥ: 'ɥ',
  Ɨ: 'ɨ',
  'Ɪ': 'ɪ',
  Ɩ: 'ɪ', // actually a capital ɩ
  'Ʝ': 'ʝ',
  Ƙ: 'ƙ',
  'Ʞ': 'ʞ',
  Ɫ: 'ɫ',
  'Ɬ': 'ɬ',
  Ɱ: 'ɱ',
  Ɯ: 'ɯ',
  Ŋ: 'ŋ',
  Ɲ: 'ɲ',
  Ƞ: 'ŋ', ƞ: 'ŋ', // obsolete, replaced by ŋ
  Ɵ: 'ɵ',
  Ƥ: 'ƥ',
  Ɋ: 'ɡ͡b', ɋ: 'ɡ͡b',  // obsolete
  Ɽ: 'ɽ',
  Ʀ: 'ʀ',
  Ʃ: 'ʃ', Σ: 'ʃ', ꚃ: 'ʃ',  // alternate upper case
  Ʈ: 'ʈ',
  Ŧ: 'ʈ',
  Ƭ: 'ƭ',
  'Ʇ': 'ǀ', // capital for obsolete ʇ, replaced by ǀ
  Ʉ: 'ʉ',
  Ʊ: 'ʊ',
  Ʋ: 'ʋ',
  Ʌ: 'ʌ',
  'Ꭓ': 'χ', 'ꭓ': 'χ', // upper & lower
  '⅄': 'ʎ',
  Ʒ: 'ʒ',
  Θ: 'θ',
  Ɂ: 'ʔ', ɂ: 'ʔ', // upper & lower

  Ꜫ: 'qʼ', ꜫ: 'qʼ', // tresillo
  Ꜭ: 'kʼ', ꜭ: 'kʼ', // cuatrillo
  Ꜯ: 'tsʼ', ꜯ: 'tsʼ',

  // \u030c - varies by language: https://en.wikipedia.org/wiki/Caron
  '\u0313': 'ʲ',  // or add a /z/
  '′': 'ʲ',
  '\u0315': 'ˀ',
}

/** capital versions of some IPA used by languages that have adopted some IPA */
export const capitalIpa = [
  'Ɑ', 'Ɒ', 'Ɐ', 'Ɓ', 'Ƃ', 'Ꞵ', 'Ɔ', 'Ƈ', 'Ð', 'Ɖ', 'Ɗ', 'Ə', 'Ǝ', 'Ɛ', 'Ɜ', 'Ɡ', 'Ɠ', 'Ɣ',
  'Ħ', 'Ɦ', 'Ɥ', 'Ɨ', 'Ɪ', 'Ɩ', 'Ʝ', 'Ƙ', 'Ʞ', 'Ɫ', 'Ɬ', 'Ɱ', 'Ɯ', 'Ŋ', 'Ɲ', 'Ƞ', 'Ɵ', 'Ƥ',
  'Ɋ', 'Ɽ', 'Ʀ', 'Ʃ', 'Σ', 'ꚃ', 'Ʈ', 'Ŧ', 'Ƭ', 'Ʇ', 'Ʉ', 'Ʊ', 'Ʋ', 'Ʌ', 'Ꭓ', '⅄', 'Ʒ',
  'Θ', 'Ɂ', 'Ꜫ', 'Ꜭ',
];

/** multiple characters (invalid combo, possible valid pieces) to valid IPA */
const multiLookup: Record<string, IpaType> = {
  ç: 'ç',
  ẋ: 'χ',
  ǃǃ: '‼',
  ǀǀ: 'ǁ',

  bᵛ: 'b͡v',
  dᶞ: 'd̪͡ð',
  dᶻ: 'd͡z',
  dᶼ: 'ɖ͡ʐ', ɖᶼ: 'ɖ͡ʐ',
  dᶽ: 'd͡ʑ',
  kˣ: 'k͡x',
  pᶠ: 'p͡f',
  pᶲ: 'p͡ɸ',
  qᵡ: 'q͡χ',
  tˢ: 't͡s',
  tᶝ: 't͡ɕ',
  tᶳ: 'ʈ͡ʂ', ʈᶳ: 'ʈ͡ʂ',
  tᶴ: 't͡ʃ',
  tᶿ: 't̪͡θ',
  ɢʶ: 'ɢ͡ʁ',
}
